import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Input, InputNumber, Form, Switch, Divider, Select } from "antd";
import _ from "lodash";
import { findIdSelector } from "../reduxSelector";
import CodeEditor from "../../../../../../helpers/CodeEditor2";
import { Modal } from "../../../../../../helpers/commonUi";
import { updateFormProperties } from "../../../../../../stateManager/store/formCreation/action";
import { updateAssetProperties } from "../../../../../../stateManager/store/assets/action";
import { DocumentColector as DocumentCollector } from "./documentCollector";
import { AddLabelValue } from "./AddLabelValue";
import { ListLabelValue } from "./ListLabelValue";
import style from "./props.module.sass";
import {
  useDebouncedAction,
  assetSelector,
} from "../../../../../../helpers/commonFunction";

const { Option } = Select;

declare const window: any;

interface DataPanelProps {
  json: any;
  canEdit: boolean;
}

export default function DataPanel({ json, canEdit }: DataPanelProps) {
  const id = json.id;
  const dispatch = useDispatch();
  const params: any = useParams();
  const formId = params.formId;
  const [ErrMessage, setErrMessage] = useState("");

  const {
    json: { pluginName, type, widgetName },
  } = json;

  const [originalConfig, setOriginalConfig] = useState<any>({});
  const [pluginConfig, setPluginConfig] = useDebouncedAction({});
  const [visible, setVisible] = useState(false);

  const selectedAssetId = useSelector(
    (state: any) => state.assets.selectedAssetId
  );
  const assets = useSelector((state: any) => state.assets.list);
  const assetEditMode = useSelector((state: any) => state.assets.assetEditMode);

  let currentState: any = useSelector((state: any) => {
    return id ? findIdSelector(state, params.formId, id.split("#"), true) : [];
  });
  currentState = assetEditMode
    ? assetSelector(assets[selectedAssetId], id.split("#"))
    : currentState;

  const options =
    (currentState && currentState.data && currentState.data.options) || [];

  const onChange = useCallback(
    (data: any, key: string) => {
      if (!canEdit) return;
      const setData = _.setWith({}, key, data, Object);
      if (assetEditMode && selectedAssetId) {
        dispatch(
          updateAssetProperties({
            assetId: selectedAssetId,
            id,
            data: setData,
          })
        );
      } else {
        dispatch(
          updateFormProperties({
            formid: formId,
            id,
            data: setData,
          })
        );
      }
    },
    [dispatch, formId, id, canEdit, assetEditMode, selectedAssetId]
  );

  useEffect(() => {
    let identifier = pluginName || type;
    identifier = identifier === "Image" ? "ImageViewer" : identifier;

    if (window[identifier]) {
      const { type: pluginType, data: plugin } = window[identifier];
      let receivedConfig: any;

      if (pluginType === "Package" && widgetName) {
        receivedConfig = plugin.widgets[widgetName].config;
      } else {
        receivedConfig = plugin.config;
      }
      const renderConfig = { ...receivedConfig };
      const data = (currentState && currentState.data) || {};

      // only keep the fields that belong to the data section
      Object.keys(renderConfig).forEach((key) => {
        const field = renderConfig[key];
        if (field.section !== "data") {
          delete renderConfig[key];
        }
      });
      setOriginalConfig(renderConfig);

      const updatedPluginConfig = Object.keys(renderConfig).reduce(
        (a: any, c: any) => {
          a[c] = data[c] ?? renderConfig[c].value ?? renderConfig[c].default;
          return a;
        },
        {}
      );
      setPluginConfig(updatedPluginConfig, () => null);
    }

    // TODO: Fix later why adding 'currentState' to the deps array results in infinite loop
    // eslint-disable-next-line
  }, [pluginName, type, widgetName]);
  return (
    <Fragment>
      {currentState &&
        currentState.type &&
        pluginConfig &&
        originalConfig &&
        Object.keys(originalConfig).map((configItem, index) => {
          const field = originalConfig[configItem];
          return (
            <div key={index}>
              {field.type === Boolean && configItem === "async" && (
                <Fragment>
                  <Form.Item
                    label={field.label}
                    className={"a8-form-item props " + style.asyncSwitch}
                  >
                    <Switch
                      data-test-id="plugin_config"
                      checked={pluginConfig[configItem]}
                      disabled={!canEdit}
                      onChange={async (e) => {
                        const finalPluginConfig = {
                          ...pluginConfig,
                          options,
                          [configItem]: e,
                        };
                        setPluginConfig(finalPluginConfig, () =>
                          onChange(finalPluginConfig, "data")
                        );
                      }}
                    />
                  </Form.Item>
                  <Divider />
                </Fragment>
              )}

              {field.type === Boolean &&
                configItem === "annotation" &&
                !pluginConfig.async && (
                  <Fragment>
                    <Divider />
                    <Form.Item
                      label={field.label}
                      className={"a8-form-item props " + style.asyncSwitch}
                    >
                      <Switch
                        data-test-id="plugin_config"
                        checked={pluginConfig[configItem]}
                        disabled={!canEdit}
                        onChange={async (e) => {
                          const finalPluginConfig = {
                            ...pluginConfig,
                            options,
                            [configItem]: e,
                          };
                          setPluginConfig(finalPluginConfig, () =>
                            onChange(finalPluginConfig, "data")
                          );
                        }}
                      />
                    </Form.Item>
                  </Fragment>
                )}

              {!pluginConfig.async && field.type === String && (
                <Form.Item label={field.label} className={"a8-form-item props"}>
                  <Input
                    placeholder={field.placeholder}
                    className={style.borderRemove}
                    disabled={!canEdit}
                    value={
                      configItem === "preload"
                        ? pluginConfig[configItem].bindParams[0]
                        : pluginConfig[configItem]
                    }
                    onChange={(e: any) => {
                      const value = e.target.value;
                      let updatedValue = value;
                      if (configItem === "preload") {
                        updatedValue = _.setWith(
                          {},
                          "bindParams",
                          [value],
                          Object
                        );
                      }
                      const finalPluginConfig = {
                        ...pluginConfig,
                        [configItem]: updatedValue,
                      };
                      setPluginConfig(finalPluginConfig, () =>
                        onChange(finalPluginConfig, "data")
                      );
                    }}
                  />
                </Form.Item>
              )}

              {!pluginConfig.async && field.type === Number && (
                <Fragment>
                  <Form.Item
                    label={field.label}
                    className={"a8-form-item props"}
                  >
                    <InputNumber
                      placeholder={field.placeholder}
                      className={style.borderRemove}
                      disabled={!canEdit}
                      min={1}
                      value={pluginConfig[configItem] || ""}
                      onChange={(value: any) => {
                        const finalPluginConfig = {
                          ...pluginConfig,
                          options,
                          [configItem]: value,
                        };
                        setPluginConfig(finalPluginConfig, () =>
                          onChange(finalPluginConfig, "data")
                        );
                      }}
                    />
                  </Form.Item>
                  {ErrMessage && (
                    <p style={{ color: "red", marginLeft: "6px" }}>
                      {ErrMessage}
                    </p>
                  )}
                </Fragment>
              )}

              {field.type === Array && (
                <Form.Item label={field.label} className={"a8-form-item props"}>
                  <Select
                    mode={field.mode || ""}
                    style={{ width: "100%" }}
                    className={style.borderRemove}
                    disabled={!canEdit}
                    value={
                      configItem === "preload"
                        ? pluginConfig[configItem].bindParams
                        : pluginConfig[configItem]
                    }
                    onChange={(value: any) => {
                      let updatedValue = value;
                      if (configItem === "preload") {
                        updatedValue = _.setWith(
                          {},
                          "bindParams",
                          value,
                          Object
                        );
                      }
                      const finalPluginConfig = {
                        ...pluginConfig,
                        [configItem]: updatedValue,
                      };
                      setPluginConfig(finalPluginConfig, () =>
                        onChange(finalPluginConfig, "data")
                      );
                    }}
                  >
                    {field.values.map((list: any, key: number) => (
                      <Option
                        data-test-id={`${list.label}`}
                        key={key}
                        value={list.value}
                      >
                        {list.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              {!pluginConfig.async &&
                (json.json.type === "Uploader"
                ? pluginConfig.annotation
                : true) &&
                pluginConfig.mode !== "async" &&
                field.type === "ArrayLabel" &&
                (json.json.type === "Uploader"
                  ? pluginConfig.annotation
                  : true) && (
                  <Fragment>
                    <AddLabelValue
                      json={currentState}
                      options={options}
                      updateOptions={onChange}
                      canEdit={canEdit}
                    />

                    <Divider />

                    <div style={{ display: "flex", flexDirection: "column" }}>
                      {options.map((option: any) => (
                        <ListLabelValue
                          data-test-id={`${options}`}
                          canEdit={canEdit}
                          key={option.id}
                          option={option}
                          options={options}
                          updateOptions={onChange}
                        />
                      ))}
                    </div>
                  </Fragment>
                )}
            </div>
          );
        })}

      {currentState && currentState.type === "DocumentCollector" && (
        <DocumentCollector
          json={currentState}
          updateOptions={onChange}
          canEdit={canEdit}
        />
      )}

      {pluginConfig && (pluginConfig.async || pluginConfig.mode === "async") && (
        <Fragment>
          <div
            data-test-id="data_panel_modal"
            className={style.codeContainer}
            onClick={() => setVisible(true)}
          >
            {pluginConfig.preload.fun.source.substr(0, 300)}
          </div>
          <Modal
            dataTestId="data_panel"
            unmountOnCancel={true}
            visible={visible}
            title="ASYNC FUNCTION"
            onCancel={() => {
              setVisible(false);
            }}
            width={"800px"}
            key="async-code-editor"
            footer={
              canEdit
                ? {
                    enable: true,
                    cancelText: "Close",
                    okText: "Save",
                    onOk: () => {
                      let event = new KeyboardEvent("keydown", {
                        key: "s",
                        code: "KeyS",
                        ctrlKey: true,
                      });
                      document.dispatchEvent(event);
                      setVisible(false);
                    },
                  }
                : {
                    enable: false,
                  }
            }
            style={{ top: "12px" }}
          >
            <CodeEditor
              onSave={(value: any) => {
                if (!canEdit) return;
                onChange(value, "data.preload.fun");
                const finalPluginConfig = {
                  ...pluginConfig,
                  preload: {
                    fun: value,
                  },
                };
                setPluginConfig(finalPluginConfig, () =>
                  onChange(finalPluginConfig, "data")
                );
              }}
              height="450px"
              lang="javascript"
              value={pluginConfig.preload.fun.source}
              saveMessage={{
                enable: true,
                message: "Async function added",
              }}
              readOnly={!canEdit}
              type="inline"
            />
          </Modal>
        </Fragment>
      )}
    </Fragment>
  );
}
